import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper, Alert } from '@mui/material';
import { supabase } from '../../config/supabase';
import { profileService } from '../../services/profileService';

/**
 * Auth callback route: /auth/callback
 * Supabase redirects here after magic link (or other OAuth) verification.
 * We finalize the session, look up profiles.role, then redirect to the appropriate app route.
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase puts tokens in the URL hash; getSession() parses them and establishes the session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setError(sessionError.message || 'Failed to restore session');
          return;
        }

        if (!session) {
          setError('No session found. The link may have expired.');
          return;
        }

        const role = await profileService.getUserRole();

        if (!role) {
          setError('Unable to determine user role.');
          return;
        }

        // Redirect based on profile.role (same logic as LoginPage)
        switch (role) {
          case 'new_hire':
            navigate('/onboarding', { replace: true });
            break;
          case 'admin':
            navigate('/dashboard', { replace: true });
            break;
          case 'owner':
            navigate('/leaderdashboard', { replace: true });
            break;
          case 'ticketmaster':
            navigate('/tickets', { replace: true });
            break;
          default:
            navigate('/dashboard', { replace: true });
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Something went wrong';
        setError(message);
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          px: 2,
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 400 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            You can try signing in again from the login page.
          </Typography>
          <Typography
            component="a"
            href="/login"
            sx={{ display: 'block', mt: 2, color: 'primary.main' }}
          >
            Back to login
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Signing you in...</Typography>
    </Box>
  );
};

export default AuthCallback;
